import * as React from "react";

import PropTypes from "prop-types";

import { StyledTableCell, StyledTableRow } from "../../../table/BaseTable/BaseTable";

import FilterFieldTable from "../../../field/FilterFieldTable";

import DeleteIcon from "@mui/icons-material/Delete";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import { IconButton, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
    DATETIME_TO_DATE_RANGE_END_FIELD_SUBTYPE,
    DATETIME_TO_DATE_RANGE_FIELD_TYPE,
    DATETIME_TO_DATE_RANGE_START_FIELD_SUBTYPE,
    DATE_RANGE_FIELD_TYPE,
    DATE_TIME_RANGE_FIELD_TYPE,
    NUMERIC_RANGE_FIELD_TYPE,
    TIME_RANGE_FIELD_TYPE,
} from "../../../../helpers/constants";
import { getFilterSearchParamsKey, getObjectWithoutEmptyValues } from "../../../../helpers/methods";
import useFilterSearchParams from "../../../../hooks/useFilterSearchParams";
import IconButtonWithTooltip from "../../../button/IconButtonWithTooltip";

const UniversalHeaderFilterRow = React.memo(
    ({
        showCheckbox,
        showDetailsIcon,
        headersConfig,
        resetPageNumber,
        filterPrefix,
        showCleanFilterIcon,
        showChangesCircle,
        excludedKeysForClearSearchParams,
        onSearchCb,
        hideFiltersIconCol,
        initialParams,
    }) => {
        const { t } = useTranslation();

        const [searchParams, setSearchParams] = useSearchParams();

        const { clearSearchParamsByFilterPrefix } = useFilterSearchParams(searchParams);

        const setFieldName = (name, filterPrefix) => {
            if (filterPrefix !== undefined) {
                return `${getFilterSearchParamsKey(filterPrefix)}${name}`;
            }
            return name;
        };

        const [searchValues, setSearchValues] = React.useState({});
        const [clearFilters, setClearFilters] = React.useState(false);

        React.useEffect(() => {
            // console.log("searchValues rerun", searchValues);
            // if (searchValues) {
            onSearchCb?.(searchValues);
            // }
        }, [searchValues, onSearchCb]);

        const onClearParams = () => {
            setSearchValues({});
            setSearchParams(
                clearSearchParamsByFilterPrefix(filterPrefix, excludedKeysForClearSearchParams),
            );
            setClearFilters(!clearFilters);
            onSearchCb?.(undefined)
        };

        const getFieldConfig = (header) => {
            return header.filterType === TIME_RANGE_FIELD_TYPE
                ? {
                      minsStep: 60,
                      hoursAsId: true,
                      withClearIcon: true,
                      idIncludeTimeOffset: true,
                      ...header.fieldConfig,
                  }
                : header.fieldConfig;
        };

        const getFilterFieldContent = (header) => {
            if (header.includeExcludedFilter) {
                return (
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <FilterFieldTable
                            name={setFieldName(header.name, filterPrefix)}
                            type={header.filterType}
                            resetPageNumber={resetPageNumber}
                            fieldConfig={getFieldConfig(header)}
                            filterPrefix={filterPrefix}
                            onSearchCb={onSearchCb}
                            setSearchValues={setSearchValues}
                            clearFilters={clearFilters}
                            initialParams={initialParams}
                        />
                        <div
                            style={{
                                display: "flex",
                                alignItems: "column",
                            }}
                        >
                            <IconButtonWithTooltip title={t("excluded")}>
                                <PriorityHighIcon sx={{ color: "var(--alert)" }} fontSize="small" />
                            </IconButtonWithTooltip>
                            <FilterFieldTable
                                clearFilters={clearFilters}
                                name={setFieldName(`${header.name}_excluded`, filterPrefix)}
                                type={header.filterType}
                                resetPageNumber={resetPageNumber}
                                fieldConfig={getFieldConfig(header)}
                                filterPrefix={filterPrefix}
                                onSearchCb={onSearchCb}
                                setSearchValues={setSearchValues}
                                initialParams={initialParams}
                            />
                        </div>
                    </div>
                );
            }
            if (
                header.filterType !== DATE_RANGE_FIELD_TYPE &&
                header.filterType !== DATE_TIME_RANGE_FIELD_TYPE &&
                header.filterType !== DATETIME_TO_DATE_RANGE_FIELD_TYPE &&
                header.filterType !== NUMERIC_RANGE_FIELD_TYPE &&
                header.filterType !== TIME_RANGE_FIELD_TYPE
            ) {
                return (
                    <FilterFieldTable
                        clearFilters={clearFilters}
                        name={setFieldName(header.name, filterPrefix)}
                        type={header.filterType}
                        resetPageNumber={resetPageNumber}
                        fieldConfig={getFieldConfig(header)}
                        filterPrefix={filterPrefix}
                        onSearchCb={onSearchCb}
                        setSearchValues={setSearchValues}
                        initialParams={initialParams}
                    />
                );
            }
            if (
                header.filterType === DATE_RANGE_FIELD_TYPE ||
                header.filterType === DATETIME_TO_DATE_RANGE_FIELD_TYPE ||
                header.filterType === DATE_TIME_RANGE_FIELD_TYPE
            ) {
                return (
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <FilterFieldTable
                            clearFilters={clearFilters}
                            name={setFieldName(`${header.name}_after`, filterPrefix)}
                            label={t("from")}
                            type={header.filterType}
                            subtype={DATETIME_TO_DATE_RANGE_START_FIELD_SUBTYPE}
                            resetPageNumber={resetPageNumber}
                            fieldConfig={getFieldConfig(header)}
                            filterPrefix={filterPrefix}
                            onSearchCb={onSearchCb}
                            setSearchValues={setSearchValues}
                            initialParams={initialParams}
                        />
                        <FilterFieldTable
                            clearFilters={clearFilters}
                            name={setFieldName(`${header.name}_before`, filterPrefix)}
                            label={t("to")}
                            type={header.filterType}
                            subtype={DATETIME_TO_DATE_RANGE_END_FIELD_SUBTYPE}
                            resetPageNumber={resetPageNumber}
                            fieldConfig={getFieldConfig(header)}
                            filterPrefix={filterPrefix}
                            onSearchCb={onSearchCb}
                            setSearchValues={setSearchValues}
                            initialParams={initialParams}
                        />
                    </div>
                );
            }
            if (header.filterType === NUMERIC_RANGE_FIELD_TYPE) {
                return (
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <FilterFieldTable
                            clearFilters={clearFilters}
                            name={setFieldName(`${header.name}_min`, filterPrefix)}
                            label={t("from")}
                            type={header.filterType}
                            resetPageNumber={resetPageNumber}
                            fieldConfig={getFieldConfig(header)}
                            filterPrefix={filterPrefix}
                            onSearchCb={onSearchCb}
                            setSearchValues={setSearchValues}
                            initialParams={initialParams}
                        />
                        <FilterFieldTable
                            clearFilters={clearFilters}
                            name={setFieldName(`${header.name}_max`, filterPrefix)}
                            label={t("to")}
                            type={header.filterType}
                            resetPageNumber={resetPageNumber}
                            fieldConfig={getFieldConfig(header)}
                            filterPrefix={filterPrefix}
                            onSearchCb={onSearchCb}
                            setSearchValues={setSearchValues}
                            initialParams={initialParams}
                        />
                    </div>
                );
            }
            return (
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <FilterFieldTable
                        clearFilters={clearFilters}
                        name={setFieldName(`${header.name}_from`, filterPrefix)}
                        label={t("from")}
                        type={header.filterType}
                        resetPageNumber={resetPageNumber}
                        fieldConfig={getFieldConfig(header)}
                        filterPrefix={filterPrefix}
                        onSearchCb={onSearchCb}
                        setSearchValues={setSearchValues}
                        initialParams={initialParams}
                    />
                    <FilterFieldTable
                        clearFilters={clearFilters}
                        name={setFieldName(`${header.name}_to`, filterPrefix)}
                        label={t("to")}
                        type={header.filterType}
                        resetPageNumber={resetPageNumber}
                        fieldConfig={getFieldConfig(header)}
                        filterPrefix={filterPrefix}
                        onSearchCb={onSearchCb}
                        setSearchValues={setSearchValues}
                        initialParams={initialParams}
                    />
                </div>
            );
        };

        const getSecondColSpan = () => {
            let colSpan = 1;
            if (showDetailsIcon) {
                colSpan += 1;
            }
            return colSpan;
        };

        return (
            <StyledTableRow>
                {showChangesCircle && (
                    <StyledTableCell
                        sx={{
                            backgroundColor: "var(--light-blue) !important",
                        }}
                    />
                )}
                {showCheckbox && (
                    <StyledTableCell
                        sx={{
                            backgroundColor: "var(--light-blue) !important",
                        }}
                    />
                )}
                {!hideFiltersIconCol && (
                    <StyledTableCell
                        colSpan={getSecondColSpan()}
                        sx={{
                            backgroundColor: "var(--light-blue) !important",
                            textAlign: "center",
                        }}
                    >
                        {showCleanFilterIcon && (
                            <Tooltip title={t("other.universal_header_filter_row.clean_filters")}>
                                <IconButton
                                    color="primary"
                                    onClick={() => onClearParams()}
                                    sx={{ padding: "5px" }}
                                >
                                    <DeleteIcon fontSize="medium" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </StyledTableCell>
                )}
                {headersConfig.map((header, index) => (
                    <StyledTableCell
                        className="capitalize-first-letter"
                        key={`${header.name}${index}`}
                        align="center"
                        sx={{
                            verticalAlign: "bottom",
                            backgroundColor: "var(--light-blue) !important",
                        }}
                    >
                        {getFilterFieldContent(header)}
                    </StyledTableCell>
                ))}
            </StyledTableRow>
        );
    },
);

export default UniversalHeaderFilterRow;

UniversalHeaderFilterRow.propTypes = {
    resetPageNumber: PropTypes.func,
    showCheckbox: PropTypes.bool,
    showDetailsIcon: PropTypes.bool,
    headersConfig: PropTypes.arrayOf(
        PropTypes.shape({
            filterType: PropTypes.string,
            name: PropTypes.string,
            label: PropTypes.string,
            fieldConfig: PropTypes.object,
        }),
    ),
    showCleanFilterIcon: PropTypes.bool,
    showChangesCircle: PropTypes.bool,
    excludedKeysForClearSearchParams: PropTypes.array,
};

UniversalHeaderFilterRow.defaultProps = {
    showCleanFilterIcon: false,
    showCheckbox: true,
    showDetailsIcon: false,
    showChangesCircle: false,
};
